/**
 * Theme constants and configuration.
 *
 * These constants are separated from App.tsx to enable Fast Refresh.
 * Non-component exports in the same file as components break HMR.
 */

/**
 * 8-point grid spacing system
 *
 * | Name    | Spacing  | Points |
 * |---------|----------|--------|
 * | 0       | 0        | 0pt    |
 * | quarter | 1px      | 2pt    | (use 0.25)
 * | half    | 0.25rem  | 4pt    | (use 0.5)
 * | 1       | 0.5rem   | 8pt    |
 * | 2       | 1rem     | 16pt   |
 * | 3       | 1.5rem   | 24pt   |
 * | 4       | 2rem     | 32pt   |
 * | 5       | 2.5rem   | 40pt   |
 * | 6       | 3rem     | 48pt   |
 * | 7       | 3.5rem   | 56pt   |
 * | 8       | 4rem     | 64pt   |
 * | 9       | 4.5rem   | 72pt   |
 * | 10      | 5rem     | 80pt   |
 * | 12      | 6rem     | 96pt   |
 * | 14      | 7rem     | 112pt  |
 * | 16      | 8rem     | 128pt  |
 * | 18      | 9rem     | 144pt  |
 * | 20      | 10rem    | 160pt  |
 *
 * Usage: theme.spacing(1) = 8px, theme.spacing(2) = 16px, etc.
 * For quarter spacing: theme.spacing(0.25) = 2px
 * For half spacing: theme.spacing(0.5) = 4px
 */
export const SPACING_UNIT = 8; // 8-point grid base unit in pixels

/**
 * Corner Radius design tokens
 *
 * | Size   | Value | Use case                          |
 * |--------|-------|-----------------------------------|
 * | small  | 8px   | Small elements, small screens     |
 * | medium | 16px  | Big elements, smaller screens     |
 * | large  | 24px  | Big elements, big screens         |
 */
export const BORDER_RADIUS = {
  small: 8,
  medium: 16,
  large: 24,
} as const;

/**
 * Brand Color Palette
 *
 * Three color families: Dahlia (green), Bellflower (purple), Daisy (yellow/gold)
 * Each family has shades: 900 (darkest), 700, 500, 300, 100, 50 (lightest)
 *
 * Usage guidelines:
 * - Dahlia: Primary brand color, success states, positive feedback
 * - Bellflower: Secondary/info, notes, neutral information
 * - Daisy: Warnings, attention, caution states
 *
 * UI Pattern (from Figma):
 * - Background: [color]50
 * - Border: [color]300
 * - Text/Icon: [color]700
 */
export const COLORS = {
  // Dahlia (Green) - Primary brand color
  dahlia: {
    900: "#1C4321", // Darkest - primary text, headers
    700: "#2E7831", // Dark - text on light backgrounds
    500: "#7EC254", // Medium - accents
    300: "#AFE57F", // Light - borders, highlights
    100: "#DBF4C2", // Very light - subtle backgrounds
    50: "#F7FCF1", // Lightest - card backgrounds
  },
  // Bellflower (Purple) - Secondary/info color
  bellflower: {
    900: "#433765", // Darkest
    700: "#7D5DA3", // Dark - text on light backgrounds
    500: "#B69BDE", // Medium - accents
    300: "#D3C9E7", // Light - borders
    100: "#E0DAEE", // Very light - subtle backgrounds
    50: "#F1EFF7", // Lightest - card backgrounds
  },
  // Daisy (Yellow/Gold) - Warning/accent color
  daisy: {
    900: "#4A411F", // Darkest
    700: "#A48137", // Dark - text on light backgrounds
    500: "#EFC52E", // Medium - accents
    300: "#F7E880", // Light - borders
    100: "#FAF1B6", // Very light - subtle backgrounds
    50: "#FDFBEB", // Lightest - card backgrounds
  },
} as const;
